import { Component, OnInit } from '@angular/core';

interface Contact {
  id: number;
  name: string;
  phone: string;
  avatar?: string;
  lastMessage?: string;
  lastMessageTime?: Date;
  unreadCount: number;
}

interface Message {
  id: number;
  contactId: number;
  text: string;
  timestamp: Date;
  isIncoming: boolean;
  read: boolean;
}

interface Conversation {
  contactId: number;
  messages: Message[];
  hasUnread: boolean;
}

@Component({
  selector: 'app-whatsapp',
  standalone: false,
  templateUrl: './whatsapp.component.html',
  styleUrl: './whatsapp.component.css'
})
export class WhatsappComponent implements OnInit {
  contacts: Contact[] = [];
  conversations: Conversation[] = [];
  selectedContact: Contact | null = null;
  selectedConversation: Conversation | null = null;
  newMessage: string = '';

  ngOnInit(): void {
    // Load mock data
    this.loadMockData();

    // Open the first unread conversation by default
    this.openFirstUnreadConversation();
  }

  loadMockData(): void {
    // Mock contacts
    this.contacts = [
      {
        id: 1,
        name: 'João Silva',
        phone: '+55 11 98765-4321',
        avatar: 'assets/avatars/avatar1.jpg',
        lastMessage: 'Olá, tudo bem?',
        lastMessageTime: new Date(2023, 5, 15, 10, 30),
        unreadCount: 3
      },
      {
        id: 2,
        name: 'Maria Oliveira',
        phone: '+55 11 91234-5678',
        avatar: 'assets/avatars/avatar2.jpg',
        lastMessage: 'Podemos marcar uma reunião amanhã?',
        lastMessageTime: new Date(2023, 5, 14, 15, 45),
        unreadCount: 0
      },
      {
        id: 3,
        name: 'Pedro Santos',
        phone: '+55 11 99876-5432',
        avatar: 'assets/avatars/avatar3.jpg',
        lastMessage: 'Obrigado pela informação!',
        lastMessageTime: new Date(2023, 5, 13, 9, 15),
        unreadCount: 1
      },
      {
        id: 4,
        name: 'Ana Costa',
        phone: '+55 11 95678-1234',
        avatar: 'assets/avatars/avatar4.jpg',
        lastMessage: 'Vou verificar e te retorno.',
        lastMessageTime: new Date(2023, 5, 12, 14, 20),
        unreadCount: 0
      },
      {
        id: 5,
        name: 'Carlos Ferreira',
        phone: '+55 11 92345-6789',
        avatar: 'assets/avatars/avatar5.jpg',
        lastMessage: 'Até amanhã!',
        lastMessageTime: new Date(2023, 5, 11, 18, 10),
        unreadCount: 0
      }
    ];

    // Mock conversations
    this.conversations = [
      {
        contactId: 1,
        hasUnread: true,
        messages: [
          {
            id: 1,
            contactId: 1,
            text: 'Olá, tudo bem?',
            timestamp: new Date(2023, 5, 15, 10, 30),
            isIncoming: true,
            read: false
          },
          {
            id: 2,
            contactId: 1,
            text: 'Estou precisando de informações sobre o projeto.',
            timestamp: new Date(2023, 5, 15, 10, 31),
            isIncoming: true,
            read: false
          },
          {
            id: 3,
            contactId: 1,
            text: 'Você poderia me ajudar?',
            timestamp: new Date(2023, 5, 15, 10, 32),
            isIncoming: true,
            read: false
          }
        ]
      },
      {
        contactId: 2,
        hasUnread: false,
        messages: [
          {
            id: 4,
            contactId: 2,
            text: 'Bom dia!',
            timestamp: new Date(2023, 5, 14, 9, 0),
            isIncoming: true,
            read: true
          },
          {
            id: 5,
            contactId: 2,
            text: 'Bom dia, Maria! Como posso ajudar?',
            timestamp: new Date(2023, 5, 14, 9, 5),
            isIncoming: false,
            read: true
          },
          {
            id: 6,
            contactId: 2,
            text: 'Podemos marcar uma reunião amanhã?',
            timestamp: new Date(2023, 5, 14, 15, 45),
            isIncoming: true,
            read: true
          }
        ]
      },
      {
        contactId: 3,
        hasUnread: true,
        messages: [
          {
            id: 7,
            contactId: 3,
            text: 'Você já enviou o relatório?',
            timestamp: new Date(2023, 5, 13, 9, 0),
            isIncoming: true,
            read: true
          },
          {
            id: 8,
            contactId: 3,
            text: 'Sim, acabei de enviar por email.',
            timestamp: new Date(2023, 5, 13, 9, 10),
            isIncoming: false,
            read: true
          },
          {
            id: 9,
            contactId: 3,
            text: 'Obrigado pela informação!',
            timestamp: new Date(2023, 5, 13, 9, 15),
            isIncoming: true,
            read: false
          }
        ]
      },
      {
        contactId: 4,
        hasUnread: false,
        messages: [
          {
            id: 10,
            contactId: 4,
            text: 'Preciso do orçamento atualizado.',
            timestamp: new Date(2023, 5, 12, 14, 0),
            isIncoming: true,
            read: true
          },
          {
            id: 11,
            contactId: 4,
            text: 'Vou verificar e te retorno.',
            timestamp: new Date(2023, 5, 12, 14, 20),
            isIncoming: true,
            read: true
          }
        ]
      },
      {
        contactId: 5,
        hasUnread: false,
        messages: [
          {
            id: 12,
            contactId: 5,
            text: 'A reunião foi confirmada para amanhã às 10h.',
            timestamp: new Date(2023, 5, 11, 18, 0),
            isIncoming: false,
            read: true
          },
          {
            id: 13,
            contactId: 5,
            text: 'Até amanhã!',
            timestamp: new Date(2023, 5, 11, 18, 10),
            isIncoming: true,
            read: true
          }
        ]
      }
    ];
  }

  selectContact(contact: Contact): void {
    this.selectedContact = contact;
    this.selectedConversation = this.conversations.find(c => c.contactId === contact.id) || null;

    // Mark messages as read
    if (this.selectedConversation) {
      this.selectedConversation.messages.forEach(message => {
        if (!message.read) {
          message.read = true;
        }
      });
      this.selectedConversation.hasUnread = false;

      // Update unread count in contacts list
      const contactIndex = this.contacts.findIndex(c => c.id === contact.id);
      if (contactIndex !== -1) {
        this.contacts[contactIndex].unreadCount = 0;
      }
    }
  }

  openFirstUnreadConversation(): void {
    // Find the first contact with unread messages
    const contactWithUnread = this.contacts.find(contact => contact.unreadCount > 0);

    if (contactWithUnread) {
      this.selectContact(contactWithUnread);
    } else if (this.contacts.length > 0) {
      // If no unread messages, select the first contact
      this.selectContact(this.contacts[0]);
    }
  }

  sendMessage(): void {
    if (!this.newMessage.trim() || !this.selectedContact) return;

    const newMsg: Message = {
      id: this.getNextMessageId(),
      contactId: this.selectedContact.id,
      text: this.newMessage,
      timestamp: new Date(),
      isIncoming: false,
      read: true
    };

    // Find the conversation or create a new one
    let conversation = this.conversations.find(c => c.contactId === this.selectedContact!.id);

    if (!conversation) {
      conversation = {
        contactId: this.selectedContact.id,
        messages: [],
        hasUnread: false
      };
      this.conversations.push(conversation);
    }

    // Add the message to the conversation
    conversation.messages.push(newMsg);

    // Update the last message in contacts list
    const contactIndex = this.contacts.findIndex(c => c.id === this.selectedContact!.id);
    if (contactIndex !== -1) {
      this.contacts[contactIndex].lastMessage = this.newMessage;
      this.contacts[contactIndex].lastMessageTime = new Date();
    }

    // Clear the input field
    this.newMessage = '';
  }

  getNextMessageId(): number {
    // Find the highest message ID and increment by 1
    let maxId = 0;
    this.conversations.forEach(conversation => {
      conversation.messages.forEach(message => {
        if (message.id > maxId) {
          maxId = message.id;
        }
      });
    });
    return maxId + 1;
  }

  getContactName(contactId: number): string {
    const contact = this.contacts.find(c => c.id === contactId);
    return contact ? contact.name : 'Unknown';
  }

  buscarIniciais(nome: string) {
    if (!nome) return '';
    const palavras = nome.trim().split(" ");
    const iniciais = palavras.map(palavra => palavra.charAt(0).toUpperCase());
    return iniciais.join("");
  }

  formatDate(date: Date): string {
    if (!date) return '';

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ontem';
    } else {
      return date.toLocaleDateString();
    }
  }
}
